/**
 * Landcover as a raster: every park, field, car park and plaza in a tile,
 * painted into a grid of class ids that the terrain shader reads.
 *
 * Why a raster rather than more draped polygons: a landcover polygon is a
 * handful of boundary vertices spanning hundreds of metres, and a big flat
 * triangle laid over a hillside either cuts through it or floats above it.
 * Resampling every polygon finely enough to follow the ground would cost more
 * geometry than the terrain itself. Painted into the terrain's own surface
 * instead, a field follows every fold of the hill for free, never z-fights,
 * and costs one byte a pixel.
 *
 * At 1024 pixels a z14 tile is 1.3 m a pixel at Swedish latitudes. The shader
 * blends neighbouring pixels by class, so edges are smooth rather than stepped.
 */

import type { LandcoverClass } from '../sources/shortbread.js';
import { LANDCOVER, LANDCOVER_PRIORITY } from '../sources/shortbread.js';
import type { TriangulatedPolygon } from './scatter.js';
import type { Plots } from './plots.js';

export const LANDCOVER_SIZE = 1024;

export interface LandcoverPolygon {
  cls: LandcoverClass;
  polys: TriangulatedPolygon[];
}

/**
 * Paint polygons into a `size`×`size` class raster covering `width`×`depth`
 * metres. Row 0 is the tile's north edge, as chunk-local Z runs south.
 */
export function rasterizeLandcover(
  polygons: LandcoverPolygon[],
  width: number,
  depth: number,
  size = LANDCOVER_SIZE,
): Uint8Array {
  const raster = new Uint8Array(size * size);
  if (polygons.length === 0 || width <= 0 || depth <= 0) return raster;

  // Low priority first, so higher-priority covers land on top. A stable sort
  // keeps source order within a class.
  const ordered = polygons.slice().sort((a, b) => LANDCOVER_PRIORITY[a.cls] - LANDCOVER_PRIORITY[b.cls]);
  const sx = size / width;
  const sz = size / depth;

  for (const { cls, polys } of ordered) {
    for (const poly of polys) {
      const v = poly.vertices;
      for (let t = 0; t + 2 < poly.indices.length; t += 3) {
        const a = poly.indices[t] * 2;
        const b = poly.indices[t + 1] * 2;
        const c = poly.indices[t + 2] * 2;
        fillTriangle(raster, size, cls,
          v[a] * sx, v[a + 1] * sz, v[b] * sx, v[b + 1] * sz, v[c] * sx, v[c + 1] * sz);
      }
    }
  }
  return raster;
}

/**
 * Paint house plots over what the map left unpainted: each garden as lawn,
 * then each driveway over its garden, in asphalt, gravel or paving.
 */
export function paintPlots(raster: Uint8Array, size: number, width: number, depth: number, plots: Plots): void {
  const sx = size / width, sz = size / depth;
  const lawn = (c: number) => c === LANDCOVER.none;
  for (const { ring, cx, cz } of plots.plots) {
    const n = ring.length / 2;
    for (let k = 0; k < n; k++) {
      const j = (k + 1) % n;
      fillTriangle(raster, size, LANDCOVER.garden, cx * sx, cz * sz,
        ring[k * 2] * sx, ring[k * 2 + 1] * sz, ring[j * 2] * sx, ring[j * 2 + 1] * sz, lawn);
    }
  }
  const drive = (c: number) => c === LANDCOVER.none || c === LANDCOVER.garden || c === LANDCOVER.urban;
  const SURFACE = [LANDCOVER.drive, LANDCOVER.gravel, LANDCOVER.paving];
  for (const d of [...plots.paths, ...plots.driveways]) {
    const len = Math.hypot(d.x1 - d.x0, d.z1 - d.z0);
    if (len < 0.1) continue;
    // Widened by half a pixel each side, so a 3 m strip stays whole at 1.3 m
    // pixels, and a path of a metre still shows at all.
    const hw = d.halfWidth + 0.5 * (width / size);
    const nx = -(d.z1 - d.z0) / len * hw, nz = (d.x1 - d.x0) / len * hw;
    const q = [d.x0 + nx, d.z0 + nz, d.x1 + nx, d.z1 + nz, d.x1 - nx, d.z1 - nz, d.x0 - nx, d.z0 - nz].map((v, i) => v * (i % 2 === 0 ? sx : sz));
    const cls = SURFACE[d.surface] ?? LANDCOVER.drive;
    fillTriangle(raster, size, cls, q[0], q[1], q[2], q[3], q[4], q[5], drive);
    fillTriangle(raster, size, cls, q[0], q[1], q[4], q[5], q[6], q[7], drive);
  }
}

/** Fill the pixels whose centres fall inside a triangle, in pixel space. */
export function fillTriangle(
  raster: Uint8Array, size: number, cls: number,
  ax: number, ay: number, bx: number, by: number, cx: number, cy: number,
  over?: (current: number) => boolean,
): void {
  const minX = Math.max(0, Math.floor(Math.min(ax, bx, cx)));
  const maxX = Math.min(size - 1, Math.ceil(Math.max(ax, bx, cx)));
  const minY = Math.max(0, Math.floor(Math.min(ay, by, cy)));
  const maxY = Math.min(size - 1, Math.ceil(Math.max(ay, by, cy)));
  if (minX > maxX || minY > maxY) return;

  const area = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
  if (Math.abs(area) < 1e-9) return;
  const sign = area > 0 ? 1 : -1;

  for (let y = minY; y <= maxY; y++) {
    const py = y + 0.5;
    for (let x = minX; x <= maxX; x++) {
      const px = x + 0.5;
      const w0 = ((bx - px) * (cy - py) - (by - py) * (cx - px)) * sign;
      const w1 = ((cx - px) * (ay - py) - (cy - py) * (ax - px)) * sign;
      const w2 = ((ax - px) * (by - py) - (ay - py) * (bx - px)) * sign;
      if (w0 >= 0 && w1 >= 0 && w2 >= 0 && (over === undefined || over(raster[y * size + x]))) raster[y * size + x] = cls;
    }
  }
}
